import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { BagButton } from './BagButton';
import { primaryNav } from '../../data/navigation';
import { useAnchorNavigation } from '../../hooks/useAnchorNavigation';

/** Shared header. Navigation later maps to the native iNavMenu. */
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const goTo = useAnchorNavigation();
  const reduceMotion = useReducedMotion();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const linkClass = 'inline-flex min-h-[44px] items-center text-[14px] font-medium text-ink transition-colors duration-150 ease-out hover:text-bronze-deep';
  return <header className="sticky top-[var(--prototype-top-offset,0px)] z-40 md:top-0 border-b border-line bg-canvas" data-id="element-426" data-mp="3t2m30h72llnc">
      <div className="mx-auto flex h-[68px] max-w-page items-center justify-between gap-4 px-5 md:h-[84px] md:px-10" data-id="element-427" data-mp="3o39fdu8dkajp">
        <Link to="/" aria-label="AuraGlow Luxe home" className="block shrink-0" data-id="element-428" data-mp="kkg4xqaw81tr">
          <BrandLogo className="w-[156.19px] md:w-[198.79px]" data-id="element-429" data-mp="flmrtyziqcu4" />
        </Link>

        <nav aria-label="Primary" className="hidden md:block" data-id="element-430" data-mp="fnprzbudxmck">
          <ul className="flex items-center gap-5 lg:gap-9" data-id="element-431" data-mp="atv1npk9y94i">
            {primaryNav.map(item => <li key={item.label} data-id="element-432" data-mp="6sjio2vwom5a">
                {item.anchor ? <a href={`/#${item.anchor}`} onClick={goTo(item.anchor)} className={linkClass} data-id="element-433" data-mp="13bqxebw9omnr">
                    {item.label}
                  </a> : <NavLink to={item.to ?? '/'} className={({
              isActive
            }) => `${linkClass} ${isActive ? 'underline decoration-bronze underline-offset-[6px]' : ''}`} data-id="element-434" data-mp="2ssco28kl4b4i">
                    {item.label}
                  </NavLink>}
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-1" data-id="element-435" data-mp="2a1uyudyynxnw">
          <BagButton data-id="element-436" data-mp="2a11wrqay5wc3" />
          <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-[3px] text-ink md:hidden" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen(v => !v)} data-id="element-437" data-mp="2a3xxfme6iz48">
            {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-438" data-mp="3uhghhqx0uvt9" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-439" data-mp="3uflsa0epwhjd" />}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-440" data-mp="3918n1qzsbwd">
        {open && <motion.nav id="mobile-menu" aria-label="Mobile" initial={reduceMotion ? false : {
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-x-0 top-full border-b border-line bg-canvas md:hidden" data-id="element-441" data-mp="1nu7oyggigmlg">
            <ul className="mx-auto max-w-page divide-y divide-line px-5" data-id="element-442" data-mp="26ls167wc6icp">
              {primaryNav.map(item => <li key={item.label} data-id="element-443" data-mp="2ghv7zr24v55z">
                  {item.anchor ? <a href={`/#${item.anchor}`} onClick={goTo(item.anchor, () => setOpen(false))} className="flex min-h-[52px] items-center font-display text-2xl text-ink" data-id="element-444" data-mp="2gfr253kobw32">
                      {item.label}
                    </a> : <Link to={item.to ?? '/'} className="flex min-h-[52px] items-center font-display text-2xl text-ink" data-id="element-445" data-mp="il4ts7nct6yr">
                      {item.label}
                    </Link>}
                </li>)}
            </ul>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}